import { useState } from "react";
import { FilePlus2, FileText, Settings, SquareCheckBig, StickyNote } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { createNote } from "@/commands/nav";
import { createTaskList } from "@/commands/tasks";
import { openCards } from "@/commands/cards";
import { useSettingsDialog } from "@/components/SettingsDialog";
import { useUi, type WorkspaceApp } from "@/store/ui";
import { TreePanel } from "./TreePanel";
import { TasksSidebar } from "@/tasks/TasksSidebar";
import { TasksApp } from "@/tasks/TasksApp";
import { CardsView } from "@/cards/CardsView";
import { CardsSidebar } from "@/cards/CardsSidebar";
import { SignInBanner } from "@/components/SignInBanner";
import { AppCenter } from "./AppShell";
import { GraphSidebar } from "@/graph/GraphSidebar";
import { useGraphView } from "@/graph/useGraphData";
import { StatusBar } from "./StatusBar";
import s from "./MobileShell.module.css";

/** The graph's drawer sidebar needs its live stats. */
function GraphDrawerSidebar() {
  const { data, full, warnings } = useGraphView("global");
  return <GraphSidebar scope="global" stats={data.stats} full={full} warnings={warnings} />;
}

/**
 * Structural mobile layout (the Obsidian lesson: never ship squeezed
 * desktop chrome to a phone): full-screen editor, tree in a drawer,
 * a thumb-reach bottom nav, status bar preserved (the trust chip matters
 * everywhere).
 */
export function MobileShell() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const openSettings = useSettingsDialog((d) => d.setOpen);
  const { activeApp, setActiveApp } = useUi();

  // WorkspaceApp, not a re-declared literal union: two sources of truth for
  // the app list is exactly how this file drifts from the desktop shell.
  const chooseApp = (app: WorkspaceApp) => {
    if (activeApp === app) setDrawerOpen((open) => !open);
    else {
      setActiveApp(app);
      setDrawerOpen(false);
    }
  };

  const createForApp = () => {
    if (activeApp === "tasks") void createTaskList();
    else if (activeApp === "cards") {
      openCards();
      setTimeout(() => document.querySelector<HTMLButtonElement>("[data-card-composer]")?.click(), 0);
    } else createNote(null);
  };

  return (
    <div className={s.shell}>
      <div className={s.center} onClickCapture={() => drawerOpen && setDrawerOpen(false)}>
        <div className={s.appColumn}>
          <SignInBanner />
          <AppCenter app={activeApp} />
        </div>
      </div>

      {drawerOpen ? (
        <>
          <div className={s.drawerBackdrop} onClick={() => setDrawerOpen(false)} />
          <aside className={s.drawer} aria-label="Sidebar" onClick={(e) => e.stopPropagation()}>
            {activeApp === "tasks" ? (
              <TasksSidebar onClose={() => setDrawerOpen(false)} />
            ) : activeApp === "cards" ? (
              <CardsSidebar onClose={() => setDrawerOpen(false)} />
            ) : activeApp === "graph" ? (
              <GraphDrawerSidebar />
            ) : (
              <TreePanel />
            )}
          </aside>
        </>
      ) : null}

      <div className={s.status}>
        <StatusBar />
      </div>

      <nav className={s.bottomNav} aria-label="Primary">
        <button
          type="button"
          className={[s.navBtn, activeApp === "notes" ? s.navActive : ""].join(" ")}
          onClick={() => chooseApp("notes")}
        >
          <FileText size={18} />
          Notes
        </button>
        <button type="button" className={[s.navBtn, activeApp === "tasks" ? s.navActive : ""].join(" ")} onClick={() => chooseApp("tasks")}>
          <SquareCheckBig size={18} />
          Tasks
        </button>
        <button type="button" className={s.navBtn} onClick={createForApp}>
          <FilePlus2 size={18} />
          New
        </button>
        <button type="button" className={[s.navBtn, activeApp === "cards" ? s.navActive : ""].join(" ")} onClick={() => chooseApp("cards")}>
          <StickyNote size={18} />
          Cards
        </button>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button type="button" className={[s.navBtn, activeApp === "graph" ? s.navActive : ""].join(" ")}>
              <Settings size={18} />
              More
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content className="hn-menu" side="top" align="end" sideOffset={6}>
              <DropdownMenu.Item className="hn-menu-item" onSelect={() => chooseApp("graph")}>
                Graph view
              </DropdownMenu.Item>
              <DropdownMenu.Item className="hn-menu-item" onSelect={() => openSettings(true)}>
                Settings
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </nav>
    </div>
  );
}
